import { cn } from "@/lib/utils";

export type StructuraAppLoadingProps = {
  productName: string;
  badge: string;
  message?: string;
  /** screen = pantalla completa; embedded = bloque dentro de una página */
  variant?: "screen" | "embedded";
  className?: string;
};

/**
 * Loader de marca Structura — anillos en sentidos opuestos + badge del producto.
 * Usar para sesión, impersonación, admin y cualquier carga de pantalla completa.
 */
export function StructuraAppLoadingScreen({
  productName,
  badge,
  message = "Cargando…",
  variant = "screen",
  className,
}: StructuraAppLoadingProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-8 bg-background",
        variant === "screen" ? "min-h-screen min-h-[100dvh]" : "min-h-[220px] py-10",
        className,
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-4">
        <div className="relative flex h-16 w-16 items-center justify-center">
          <svg
            className="absolute inset-0 h-16 w-16 animate-spin text-primary"
            style={{ animationDuration: "1.4s" }}
            viewBox="0 0 64 64"
            fill="none"
            aria-hidden
          >
            <circle cx="32" cy="32" r="28" className="stroke-primary/15" strokeWidth="3" />
            <path
              d="M32 4a28 28 0 0 1 28 28"
              className="stroke-primary"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
          <svg
            className="absolute inset-2 h-12 w-12 animate-spin text-primary"
            style={{ animationDuration: "2.2s", animationDirection: "reverse" }}
            viewBox="0 0 48 48"
            fill="none"
            aria-hidden
          >
            <circle cx="24" cy="24" r="20" className="stroke-primary/10" strokeWidth="2" />
            <path
              d="M24 4a20 20 0 0 1 14.14 5.86"
              className="stroke-primary/50"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 shadow-lg">
            <span className="text-xs font-bold text-primary">{badge}</span>
          </div>
        </div>

        <div className="flex flex-col items-center gap-1.5 text-center">
          <p className="text-base font-semibold tracking-wide text-foreground">{productName}</p>
          <p className="text-xs text-muted-foreground/70">{message}</p>
        </div>
      </div>

      <div className="flex items-center gap-2" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-1.5 w-1.5 rounded-full bg-primary/50"
            style={{
              animation: "pulse 1.4s ease-in-out infinite",
              animationDelay: `${i * 0.22}s`,
            }}
          />
        ))}
      </div>

      {variant === "screen" ? (
        <div
          className="pointer-events-none fixed inset-0 -z-10"
          style={{
            background:
              "radial-gradient(ellipse 60% 40% at 50% 50%, color-mix(in oklch, var(--primary) 4%, transparent) 0%, transparent 70%)",
          }}
        />
      ) : null}
    </div>
  );
}

export const FM_APP_LOADING_BRAND = {
  productName: "Structura Market",
  badge: "SM",
} as const;
